import { atom, useAtom } from "jotai";
import { useCallback } from "react";
import PageService from "utilities/services/notebooks/page-service";

// Atom to store the mostRecentlyEditedOn timestamp
const mostRecentlyEditedOnAtom = atom<Date | undefined>(undefined);

interface UseMostRecentlyEditedOnResult {
    mostRecentlyEditedOn: Date | undefined;
    refreshMostRecentlyEditedOn: () => Promise<void>;
    setMostRecentlyEditedOn: (date: Date | undefined) => void;
}

/**
 * Hook to manage the mostRecentlyEditedOn timestamp for a page.
 * Provides a refresh function to fetch the latest timestamp from the server.
 */
const useMostRecentlyEditedOn = (
    pageId: number | null | undefined
): UseMostRecentlyEditedOnResult => {
    const [mostRecentlyEditedOn, setMostRecentlyEditedOn] = useAtom(
        mostRecentlyEditedOnAtom
    );
    const { get: getPageApi } = PageService.useGet();

    const refreshMostRecentlyEditedOn = useCallback(async () => {
        if (pageId == null || isNaN(pageId)) {
            return;
        }

        try {
            const result = await getPageApi({ id: pageId });

            if (result.result?.hasErrors() || result.resultObject == null) {
                return;
            }

            const page = result.resultObject;
            if (page.mostRecentlyEditedOn) {
                setMostRecentlyEditedOn(new Date(page.mostRecentlyEditedOn));
            }
        } catch (error) {
            console.error("Failed to refresh mostRecentlyEditedOn:", error);
        }
    }, [pageId, getPageApi, setMostRecentlyEditedOn]);

    return {
        mostRecentlyEditedOn,
        refreshMostRecentlyEditedOn,
        setMostRecentlyEditedOn,
    };
};

export default useMostRecentlyEditedOn;
